import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, Button, CircularProgress, Switch, Typography } from '@mui/material';
import TextFieldCDS from '@sharedCR/coreDesignSystem/form/TextFieldCDS';
import { aiExpressionAuthoringApi } from 'app/services/api';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FeatureFlag } from 'constants/featureFlags';
import { getUUID } from 'utils/uuid';

import { closeDialogAction } from 'store/global/actions';

import AddClauseInput from './AddClauseInput';
import ApplyFeedback from './ApplyFeedback';
import ApplyUndoToast from './ApplyUndoToast';
import AssumptionItem from './AssumptionItem';
import CandidatesGrid from './CandidatesGrid';
import ClarifyOptionCard from './ClarifyOptionCard';
import ExpressionPreview from './ExpressionPreview';
import {
  GENERATE_APPLY_BUTTON_LABEL,
  GENERATE_ASSUMPTIONS_LABEL,
  GENERATE_CANCEL_BUTTON_LABEL,
  GENERATE_CLARIFY_ANSWER_LABEL,
  GENERATE_CLARIFY_ANSWER_PLACEHOLDER,
  GENERATE_CLARIFY_FREEFORM_PROMPT_LABEL,
  GENERATE_CLARIFY_FREEFORM_TOGGLE_LABEL,
  GENERATE_CLARIFY_LABEL,
  GENERATE_CLARIFY_SUBMIT_BUTTON_LABEL,
  GENERATE_DESCRIPTION_LABEL,
  GENERATE_DESCRIPTION_PLACEHOLDER,
  GENERATE_FAILED_HELPER,
  GENERATE_N_BEST_DEFAULT_REQUEST,
  GENERATE_OUTCOME,
  GENERATE_PREVIEW_LABEL,
  GENERATE_PREVIEW_UNAVAILABLE_HELPER,
  GENERATE_REFINE_BACK_BUTTON_LABEL,
  GENERATE_REFINE_BUTTON_LABEL,
  GENERATE_REFINE_HELPER,
  GENERATE_REFINE_LABEL,
  GENERATE_REFINE_PLACEHOLDER,
  GENERATE_REFINE_SUBMIT_BUTTON_LABEL,
  GENERATE_RETRY_BUTTON_LABEL,
  GENERATE_SHOW_OPTIONS_HELPER,
  GENERATE_SHOW_OPTIONS_TOGGLE_LABEL,
  GENERATE_STATUS,
  GENERATE_SUBMIT_BUTTON_LABEL,
  GENERATE_SUMMARY_LABEL,
  GENERATE_VALIDATION_ERRORS_LABEL,
  GENERATE_WORKING_HELPER,
} from './GenerateExpression.constants';
import {
  assumptionsListStyles,
  assumptionsWrapperStyles,
  clarifyFreeformToggleStyles,
  clarifyFreeformWrapperStyles,
  clarifyOptionListStyles,
  modalBodyWrapperStyles,
  modalButtonRowStyles,
  modalDescriptionFieldStyles,
  modalSectionLabelStyles,
  modalStatusRowStyles,
  previewBoxStyles,
  refinePromptWrapperStyles,
  showOptionsHelperStyles,
  showOptionsToggleRowStyles,
  validationErrorListStyles,
  validationErrorsWrapperStyles,
  workingRowSpacerStyles,
} from './GenerateExpression.styles';
import { GenerateExpressionTestIds } from './GenerateExpression.testIds';
import {
  astToUserFriendlyText,
  isCancelledError,
  normalizeClarifyOptions,
  resolveDefaultClarifyOptionIndex,
  resolveExpectedRootTypeName,
} from './GenerateExpression.utils';
import useGenerateExpressionProgress from './hooks/useGenerateExpressionProgress';
import { parseCurrentExpressionToAst, parseProposedAst } from './previewClauses.utils';
import ProgressTrail from './ProgressTrail';
import ValidationErrorItem from './ValidationErrorItem';

const renderValidationErrorItem = message => (
  <ValidationErrorItem key={message} message={message} />
);

const renderValidationErrors = errors => (
  <Box
    data-testid={GenerateExpressionTestIds.MODAL_VALIDATION_ERRORS}
    sx={validationErrorsWrapperStyles}
  >
    <Typography color="error.dark" sx={modalSectionLabelStyles} variant="bold12">
      {GENERATE_VALIDATION_ERRORS_LABEL}
    </Typography>
    <Box component="ul" sx={validationErrorListStyles}>
      {errors.map(renderValidationErrorItem)}
    </Box>
  </Box>
);

const renderAssumptionItem = message => <AssumptionItem key={message} message={message} />;

const renderAssumptions = items => (
  <Box data-testid={GenerateExpressionTestIds.MODAL_ASSUMPTIONS} sx={assumptionsWrapperStyles}>
    <Typography color="warning.dark" sx={modalSectionLabelStyles} variant="bold12">
      {GENERATE_ASSUMPTIONS_LABEL}
    </Typography>
    <Box component="ul" sx={assumptionsListStyles}>
      {items.map(renderAssumptionItem)}
    </Box>
  </Box>
);

const GenerateExpressionModalBody = ({
  expectedRootTypeName: expectedRootTypeNameProp = null,
  schemaDomain,
  schemaJson = null,
  schemaParams = null,
  currentExpressionText = null,
  isOnlyBooleanAllowed = false,
  onApplyExpression,
  onUndoApplyExpression = null,
}) => {
  const dispatch = useDispatch();
  const isNewExpressionsParsingLogicEnabled = useFeatureToggle(
    FeatureFlag.EnableNewExpressionsParsingLogic,
  );

  const expectedRootTypeName =
    expectedRootTypeNameProp ?? resolveExpectedRootTypeName({ isOnlyBooleanAllowed });

  const [status, setStatus] = useState(GENERATE_STATUS.INPUT);
  const [description, setDescription] = useState('');
  const [result, setResult] = useState(null);
  const [threadMessages, setThreadMessages] = useState(null);
  const [pendingAnswer, setPendingAnswer] = useState('');
  const [isFreeformExpanded, setIsFreeformExpanded] = useState(false);
  const [previewText, setPreviewText] = useState(null);
  const [applyError, setApplyError] = useState(null);
  const [isShowOptionsEnabled, setIsShowOptionsEnabled] = useState(false);
  const [refinementInput, setRefinementInput] = useState('');
  const [isApplied, setIsApplied] = useState(false);
  const [appliedPreviousExpressionText, setAppliedPreviousExpressionText] = useState(null);
  const [isUndoToastVisible, setIsUndoToastVisible] = useState(false);
  const [activeRequestId, setActiveRequestId] = useState(null);

  const currentExpression = useMemo(
    () =>
      parseCurrentExpressionToAst({
        currentExpressionText,
        schemaParams,
        isOnlyBooleanAllowed,
        isNewExpressionsParsingLogicEnabled,
      }),
    [
      currentExpressionText,
      schemaParams,
      isOnlyBooleanAllowed,
      isNewExpressionsParsingLogicEnabled,
    ],
  );

  const candidates = useMemo(() => result?.candidates ?? [], [result]);

  const abortControllerRef = useRef(null);

  const liveProgressEvents = useGenerateExpressionProgress({
    requestId: activeRequestId,
    isActive: status === GENERATE_STATUS.WORKING,
  });

  const finalProgressEvents = useMemo(() => result?.progressEvents ?? null, [result]);

  const cancelInflightRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  useEffect(() => () => cancelInflightRequest(), [cancelInflightRequest]);

  const handleResult = useCallback(
    data => {
      const outcome = data?.outcome;
      const nextThreadMessages = data?.threadMessages ?? null;

      setResult(data);
      setThreadMessages(nextThreadMessages);
      setIsApplied(false);

      if (outcome === GENERATE_OUTCOME.FINAL) {
        const nextCandidates = data?.candidates ?? null;

        if (!_.isNil(nextCandidates) && nextCandidates.length > 1) {
          setPreviewText(null);
          setApplyError(null);
          setStatus(GENERATE_STATUS.CANDIDATES);

          return;
        }

        const text = astToUserFriendlyText(data?.expressionJson, {
          isNewExpressionsParsingLogicEnabled,
        });
        setPreviewText(text);
        setApplyError(null);
        setStatus(GENERATE_STATUS.PREVIEW);

        return;
      }

      if (outcome === GENERATE_OUTCOME.NEEDS_USER_INPUT) {
        setPendingAnswer('');
        setIsFreeformExpanded(false);
        setStatus(GENERATE_STATUS.CLARIFY);

        return;
      }

      setStatus(GENERATE_STATUS.FAILED);
    },
    [isNewExpressionsParsingLogicEnabled],
  );

  const sendGenerateRequest = useCallback(
    ({
      resumeWithUserAnswer = null,
      resumeThreadMessages = null,
      refinementInstruction = null,
      maxCandidates = null,
    } = {}) => {
      cancelInflightRequest();

      const controller = new AbortController();
      abortControllerRef.current = controller;
      const requestId = getUUID();

      setActiveRequestId(requestId);
      setStatus(GENERATE_STATUS.WORKING);
      setApplyError(null);

      aiExpressionAuthoringApi
        .generateExpression({
          schemaDomain,
          expectedRootTypeName,
          description,
          currentExpressionJson: currentExpression?.json ?? null,
          schemaJson,
          resumeWithUserAnswer,
          resumeThreadMessages,
          refinementInstruction,
          maxCandidates,
          requestId,
          signal: controller.signal,
        })
        .then(({ data }) => {
          if (controller.signal.aborted) {
            return;
          }

          handleResult(data);
        })
        .catch(error => {
          if (isCancelledError(error) || controller.signal.aborted) {
            return;
          }

          setResult(null);
          setStatus(GENERATE_STATUS.FAILED);
        });
    },
    [
      cancelInflightRequest,
      schemaDomain,
      expectedRootTypeName,
      description,
      currentExpression,
      schemaJson,
      handleResult,
    ],
  );

  const onDescriptionChange = useCallback(event => {
    setDescription(event.target.value);
  }, []);

  const onSubmit = useCallback(() => {
    if (_.isEmpty(_.trim(description))) {
      return;
    }

    sendGenerateRequest({
      maxCandidates: isShowOptionsEnabled ? GENERATE_N_BEST_DEFAULT_REQUEST : null,
    });
  }, [description, isShowOptionsEnabled, sendGenerateRequest]);

  const onToggleShowOptions = useCallback(event => {
    setIsShowOptionsEnabled(event.target.checked);
  }, []);

  const onCancel = useCallback(() => {
    cancelInflightRequest();
    dispatch(closeDialogAction());
  }, [cancelInflightRequest, dispatch]);

  const onRefine = useCallback(() => {
    if (_.isNil(threadMessages) || _.isEmpty(threadMessages)) {
      cancelInflightRequest();
      setResult(null);
      setThreadMessages(null);
      setPreviewText(null);
      setApplyError(null);
      setStatus(GENERATE_STATUS.INPUT);

      return;
    }

    setRefinementInput('');
    setApplyError(null);
    setStatus(GENERATE_STATUS.REFINING);
  }, [cancelInflightRequest, threadMessages]);

  const onRefinementInputChange = useCallback(event => {
    setRefinementInput(event.target.value);
  }, []);

  const onSubmitRefinement = useCallback(() => {
    const trimmed = _.trim(refinementInput);

    if (_.isEmpty(trimmed)) {
      return;
    }

    sendGenerateRequest({
      refinementInstruction: trimmed,
      resumeThreadMessages: threadMessages,
    });
  }, [refinementInput, sendGenerateRequest, threadMessages]);

  const onCancelRefinement = useCallback(() => {
    setRefinementInput('');
    setStatus(GENERATE_STATUS.PREVIEW);
  }, []);

  const onRetry = useCallback(() => {
    cancelInflightRequest();
    setResult(null);
    setThreadMessages(null);
    setApplyError(null);

    if (_.isEmpty(_.trim(description))) {
      setStatus(GENERATE_STATUS.INPUT);

      return;
    }

    sendGenerateRequest();
  }, [cancelInflightRequest, description, sendGenerateRequest]);

  const onApply = useCallback(() => {
    if (_.isNil(previewText)) {
      setApplyError(GENERATE_PREVIEW_UNAVAILABLE_HELPER);

      return;
    }

    const applied = onApplyExpression(result?.expressionJson);

    if (!applied) {
      setApplyError(GENERATE_PREVIEW_UNAVAILABLE_HELPER);

      return;
    }

    setAppliedPreviousExpressionText(currentExpressionText);
    setIsApplied(true);
    setIsUndoToastVisible(true);
  }, [currentExpressionText, onApplyExpression, previewText, result]);

  const onUndoToastDismiss = useCallback(() => {
    setIsUndoToastVisible(false);
  }, []);

  const onUndoApply = useCallback(() => {
    if (!_.isNil(onUndoApplyExpression)) {
      onUndoApplyExpression(appliedPreviousExpressionText);
    }

    setIsUndoToastVisible(false);
    setIsApplied(false);
  }, [appliedPreviousExpressionText, onUndoApplyExpression]);

  const onSubmitAddClause = useCallback(
    clauseDescription => {
      sendGenerateRequest({
        refinementInstruction: `Add a clause: ${clauseDescription}`,
        resumeThreadMessages: threadMessages,
      });
    },
    [sendGenerateRequest, threadMessages],
  );

  const onSelectCandidate = useCallback(
    candidate => {
      const text = astToUserFriendlyText(candidate?.expressionJson, {
        isNewExpressionsParsingLogicEnabled,
      });
      setResult({
        outcome: GENERATE_OUTCOME.FINAL,
        expressionJson: candidate?.expressionJson,
        summary: candidate?.summary,
        assumptions: candidate?.assumptions,
        threadMessages,
        aiUsageMetricsId: candidate?.aiUsageMetricsId,
      });
      setPreviewText(text);
      setApplyError(null);
      setIsApplied(false);
      setStatus(GENERATE_STATUS.PREVIEW);
    },
    [isNewExpressionsParsingLogicEnabled, threadMessages],
  );

  const submitClarifyAnswer = useCallback(
    answer => {
      const trimmed = _.trim(answer);

      if (_.isEmpty(trimmed)) {
        return;
      }

      sendGenerateRequest({
        resumeWithUserAnswer: trimmed,
        resumeThreadMessages: threadMessages,
      });
    },
    [sendGenerateRequest, threadMessages],
  );

  const onSelectOption = useCallback(value => submitClarifyAnswer(value), [submitClarifyAnswer]);

  const onAnswerChange = useCallback(event => {
    setPendingAnswer(event.target.value);
  }, []);

  const onSubmitAnswer = useCallback(
    () => submitClarifyAnswer(pendingAnswer),
    [pendingAnswer, submitClarifyAnswer],
  );

  const onToggleFreeform = useCallback(() => {
    setIsFreeformExpanded(value => !value);
  }, []);

  const isDescriptionEmpty = _.isEmpty(_.trim(description));
  const validationErrors = result?.validationErrors ?? null;
  const summary = result?.summary ?? null;
  const assumptions = result?.assumptions ?? null;
  const errorMessage = result?.errorMessage ?? null;
  const pendingQuestion = result?.pendingQuestion ?? null;

  const proposedAst = useMemo(() => parseProposedAst(result?.expressionJson), [result]);

  const clarifyOptions = useMemo(
    () => normalizeClarifyOptions(pendingQuestion?.options),
    [pendingQuestion],
  );
  const defaultClarifyOptionIndex = useMemo(
    () => resolveDefaultClarifyOptionIndex(pendingQuestion?.defaultOptionIndex, clarifyOptions),
    [pendingQuestion, clarifyOptions],
  );
  const hasClarifyOptions = !_.isEmpty(clarifyOptions);
  const shouldShowFreeformField = !hasClarifyOptions || isFreeformExpanded;

  return (
    <Box data-testid={GenerateExpressionTestIds.MODAL_BODY} sx={modalBodyWrapperStyles}>
      {status === GENERATE_STATUS.INPUT && (
        <>
          <Typography color="text.secondary" variant="regular14">
            {GENERATE_DESCRIPTION_LABEL}
          </Typography>
          <TextFieldCDS
            multiline
            ariaLabel={GENERATE_DESCRIPTION_PLACEHOLDER}
            customStyles={modalDescriptionFieldStyles}
            dataTestId={GenerateExpressionTestIds.MODAL_DESCRIPTION_INPUT}
            maxRows={8}
            name="expressionGenerateDescription"
            placeholder={GENERATE_DESCRIPTION_PLACEHOLDER}
            rows={4}
            value={description}
            onChange={onDescriptionChange}
          />
          {!_.isNil(currentExpressionText) && !_.isEmpty(currentExpressionText) && (
            <Box>
              <Typography color="text.secondary" sx={modalSectionLabelStyles} variant="bold12">
                {GENERATE_PREVIEW_LABEL}
              </Typography>
              <Box sx={previewBoxStyles}>{currentExpressionText}</Box>
            </Box>
          )}
          <Box sx={showOptionsToggleRowStyles}>
            <Switch
              checked={isShowOptionsEnabled}
              data-testid={GenerateExpressionTestIds.MODAL_SHOW_OPTIONS_TOGGLE}
              size="small"
              onChange={onToggleShowOptions}
            />
            <Typography color="text.primary" variant="regular14">
              {GENERATE_SHOW_OPTIONS_TOGGLE_LABEL}
            </Typography>
            <Typography color="text.secondary" sx={showOptionsHelperStyles} variant="regular12">
              {GENERATE_SHOW_OPTIONS_HELPER}
            </Typography>
          </Box>
          <Box sx={modalButtonRowStyles}>
            <Button
              color="inherit"
              data-testid={GenerateExpressionTestIds.MODAL_CANCEL}
              variant="text"
              onClick={onCancel}
            >
              {GENERATE_CANCEL_BUTTON_LABEL}
            </Button>
            <Button
              data-testid={GenerateExpressionTestIds.MODAL_SUBMIT}
              disabled={isDescriptionEmpty}
              variant="contained"
              onClick={onSubmit}
            >
              {GENERATE_SUBMIT_BUTTON_LABEL}
            </Button>
          </Box>
        </>
      )}

      {status === GENERATE_STATUS.WORKING && (
        <>
          <Box data-testid={GenerateExpressionTestIds.MODAL_WORKING} sx={modalStatusRowStyles}>
            <CircularProgress size={20} />
            <Typography color="text.secondary" variant="regular14">
              {GENERATE_WORKING_HELPER}
            </Typography>
            <Box sx={workingRowSpacerStyles} />
            <Button
              color="inherit"
              data-testid={GenerateExpressionTestIds.MODAL_CANCEL}
              variant="text"
              onClick={onCancel}
            >
              {GENERATE_CANCEL_BUTTON_LABEL}
            </Button>
          </Box>
          <ProgressTrail isLive events={liveProgressEvents} />
        </>
      )}

      {status === GENERATE_STATUS.PREVIEW && (
        <>
          {!_.isEmpty(_.trim(summary)) && (
            <Box>
              <Typography color="text.secondary" sx={modalSectionLabelStyles} variant="bold12">
                {GENERATE_SUMMARY_LABEL}
              </Typography>
              <Typography
                component="div"
                data-testid={GenerateExpressionTestIds.MODAL_SUMMARY}
                variant="regular14"
              >
                {summary}
              </Typography>
            </Box>
          )}

          {!_.isEmpty(assumptions) && renderAssumptions(assumptions)}

          <Box data-testid={GenerateExpressionTestIds.MODAL_PREVIEW}>
            <Typography color="text.secondary" sx={modalSectionLabelStyles} variant="bold12">
              {GENERATE_PREVIEW_LABEL}
            </Typography>
            {_.isNil(proposedAst) ? (
              <Box sx={previewBoxStyles}>
                {_.isNil(previewText) ? GENERATE_PREVIEW_UNAVAILABLE_HELPER : previewText}
              </Box>
            ) : (
              <ExpressionPreview
                emptyHelperText={GENERATE_PREVIEW_UNAVAILABLE_HELPER}
                isNewExpressionsParsingLogicEnabled={isNewExpressionsParsingLogicEnabled}
                previousAst={currentExpression?.ast ?? null}
                proposedAst={proposedAst}
                schemaDomain={schemaDomain}
                schemaJson={schemaJson}
              />
            )}
            {!_.isNil(proposedAst) && (
              <AddClauseInput isDisabled={isApplied} onSubmit={onSubmitAddClause} />
            )}
          </Box>

          {!_.isNil(applyError) && (
            <Typography color="error.main" variant="regular14">
              {applyError}
            </Typography>
          )}

          <ProgressTrail events={finalProgressEvents} />

          <Box sx={modalButtonRowStyles}>
            <Button
              data-testid={GenerateExpressionTestIds.MODAL_REFINE}
              variant="outlined"
              onClick={onRefine}
            >
              {GENERATE_REFINE_BUTTON_LABEL}
            </Button>
            <Button
              data-testid={GenerateExpressionTestIds.MODAL_APPLY}
              disabled={_.isNil(previewText)}
              variant="contained"
              onClick={onApply}
            >
              {GENERATE_APPLY_BUTTON_LABEL}
            </Button>
          </Box>

          {isApplied && isUndoToastVisible && (
            <ApplyUndoToast onDismiss={onUndoToastDismiss} onUndo={onUndoApply} />
          )}

          {isApplied && !isUndoToastVisible && !_.isNil(result?.aiUsageMetricsId) && (
            <ApplyFeedback aiUsageMetricsId={result.aiUsageMetricsId} />
          )}
        </>
      )}

      {status === GENERATE_STATUS.CANDIDATES && (
        <>
          {!_.isEmpty(_.trim(summary)) && (
            <Box>
              <Typography color="text.secondary" sx={modalSectionLabelStyles} variant="bold12">
                {GENERATE_SUMMARY_LABEL}
              </Typography>
              <Typography component="div" variant="regular14">
                {summary}
              </Typography>
            </Box>
          )}

          <CandidatesGrid
            candidates={candidates}
            isNewExpressionsParsingLogicEnabled={isNewExpressionsParsingLogicEnabled}
            schemaDomain={schemaDomain}
            schemaJson={schemaJson}
            onSelect={onSelectCandidate}
          />

          <ProgressTrail events={finalProgressEvents} />

          <Box sx={modalButtonRowStyles}>
            <Button
              color="inherit"
              data-testid={GenerateExpressionTestIds.MODAL_CANCEL}
              variant="text"
              onClick={onCancel}
            >
              {GENERATE_CANCEL_BUTTON_LABEL}
            </Button>
          </Box>
        </>
      )}

      {status === GENERATE_STATUS.REFINING && (
        <Box sx={refinePromptWrapperStyles}>
          <Typography color="text.secondary" sx={modalSectionLabelStyles} variant="bold12">
            {GENERATE_REFINE_LABEL}
          </Typography>
          <Typography color="text.secondary" variant="regular12">
            {GENERATE_REFINE_HELPER}
          </Typography>
          <TextFieldCDS
            multiline
            ariaLabel={GENERATE_REFINE_PLACEHOLDER}
            customStyles={modalDescriptionFieldStyles}
            dataTestId={GenerateExpressionTestIds.MODAL_REFINE_INPUT}
            maxRows={6}
            name="expressionGenerateRefine"
            placeholder={GENERATE_REFINE_PLACEHOLDER}
            rows={3}
            value={refinementInput}
            onChange={onRefinementInputChange}
          />
          <Box sx={modalButtonRowStyles}>
            <Button
              color="inherit"
              data-testid={GenerateExpressionTestIds.MODAL_REFINE_BACK}
              variant="text"
              onClick={onCancelRefinement}
            >
              {GENERATE_REFINE_BACK_BUTTON_LABEL}
            </Button>
            <Button
              data-testid={GenerateExpressionTestIds.MODAL_REFINE_SUBMIT}
              disabled={_.isEmpty(_.trim(refinementInput))}
              variant="contained"
              onClick={onSubmitRefinement}
            >
              {GENERATE_REFINE_SUBMIT_BUTTON_LABEL}
            </Button>
          </Box>
        </Box>
      )}

      {status === GENERATE_STATUS.CLARIFY && (
        <Box data-testid={GenerateExpressionTestIds.MODAL_CLARIFY} sx={modalBodyWrapperStyles}>
          <Typography color="text.secondary" sx={modalSectionLabelStyles} variant="bold12">
            {GENERATE_CLARIFY_LABEL}
          </Typography>
          <Typography
            component="div"
            data-testid={GenerateExpressionTestIds.MODAL_CLARIFY_QUESTION}
            variant="regular14"
          >
            {pendingQuestion?.question}
          </Typography>

          <ProgressTrail events={finalProgressEvents} />

          {hasClarifyOptions && (
            <Box
              data-testid={GenerateExpressionTestIds.MODAL_CLARIFY_OPTION_LIST}
              sx={clarifyOptionListStyles}
            >
              {clarifyOptions.map((option, index) => (
                <ClarifyOptionCard
                  isRecommended={index === defaultClarifyOptionIndex}
                  key={option.value}
                  option={option}
                  onSelect={onSelectOption}
                />
              ))}
            </Box>
          )}

          {hasClarifyOptions && !isFreeformExpanded && (
            <Button
              color="primary"
              data-testid={GenerateExpressionTestIds.MODAL_CLARIFY_FREEFORM_TOGGLE}
              sx={clarifyFreeformToggleStyles}
              variant="text"
              onClick={onToggleFreeform}
            >
              {GENERATE_CLARIFY_FREEFORM_TOGGLE_LABEL}
            </Button>
          )}

          {shouldShowFreeformField && (
            <Box sx={clarifyFreeformWrapperStyles}>
              <Typography color="text.secondary" variant="regular14">
                {hasClarifyOptions
                  ? GENERATE_CLARIFY_FREEFORM_PROMPT_LABEL
                  : GENERATE_CLARIFY_ANSWER_LABEL}
              </Typography>
              <TextFieldCDS
                multiline
                ariaLabel={GENERATE_CLARIFY_ANSWER_PLACEHOLDER}
                customStyles={modalDescriptionFieldStyles}
                dataTestId={GenerateExpressionTestIds.MODAL_CLARIFY_ANSWER_INPUT}
                maxRows={4}
                name="expressionGenerateClarifyAnswer"
                placeholder={GENERATE_CLARIFY_ANSWER_PLACEHOLDER}
                rows={2}
                value={pendingAnswer}
                onChange={onAnswerChange}
              />
            </Box>
          )}

          <Box sx={modalButtonRowStyles}>
            <Button
              color="inherit"
              data-testid={GenerateExpressionTestIds.MODAL_CANCEL}
              variant="text"
              onClick={onCancel}
            >
              {GENERATE_CANCEL_BUTTON_LABEL}
            </Button>
            {shouldShowFreeformField && (
              <Button
                data-testid={GenerateExpressionTestIds.MODAL_CLARIFY_SUBMIT}
                disabled={_.isEmpty(_.trim(pendingAnswer))}
                variant="contained"
                onClick={onSubmitAnswer}
              >
                {GENERATE_CLARIFY_SUBMIT_BUTTON_LABEL}
              </Button>
            )}
          </Box>
        </Box>
      )}

      {status === GENERATE_STATUS.FAILED && (
        <Box data-testid={GenerateExpressionTestIds.MODAL_FAILED} sx={modalBodyWrapperStyles}>
          <Typography color="error.main" variant="regular14">
            {!_.isEmpty(_.trim(errorMessage)) ? errorMessage : GENERATE_FAILED_HELPER}
          </Typography>

          {!_.isEmpty(validationErrors) && renderValidationErrors(validationErrors)}

          <ProgressTrail events={finalProgressEvents} />

          <Box sx={modalButtonRowStyles}>
            <Button
              color="inherit"
              data-testid={GenerateExpressionTestIds.MODAL_CANCEL}
              variant="text"
              onClick={onCancel}
            >
              {GENERATE_CANCEL_BUTTON_LABEL}
            </Button>
            <Button
              data-testid={GenerateExpressionTestIds.MODAL_RETRY}
              variant="contained"
              onClick={onRetry}
            >
              {GENERATE_RETRY_BUTTON_LABEL}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

GenerateExpressionModalBody.propTypes = {
  schemaDomain: PropTypes.string.isRequired,
  onApplyExpression: PropTypes.func.isRequired,
  expectedRootTypeName: PropTypes.string,
  schemaJson: PropTypes.string,
  schemaParams: PropTypes.object,
  currentExpressionText: PropTypes.string,
  isOnlyBooleanAllowed: PropTypes.bool,
  onUndoApplyExpression: PropTypes.func,
};

export default GenerateExpressionModalBody;
